import { useCallback, useEffect, useState } from "react";
import { CheckCircle, KeyRound } from "lucide-react";
import api from "../api";

export function useEmailOtp({ email, purpose = "email_verify", role = "", onError }) {
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [info, setInfo] = useState("");

  useEffect(() => {
    setOtp("");
    setSent(false);
    setVerified(false);
    setInfo("");
  }, [email, purpose, role]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = useCallback(async () => {
    const clean = String(email || "").trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      onError?.("Please enter a valid email address first.");
      return false;
    }
    setBusy(true);
    onError?.("");
    try {
      const path = purpose === "forgot_password" ? "/auth/forgot-password" : "/auth/otp/send";
      const res = await api.post(path, { email: clean, purpose, role });
      setSent(true);
      setVerified(false);
      setOtp("");
      setCooldown(60);
      setInfo(res?.message || "OTP sent to your email. Check inbox and spam/junk folder.");
      return true;
    } catch (err) {
      onError?.(err.message || "Could not send OTP. Please try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [email, purpose, role, onError]);

  const verify = useCallback(async () => {
    const clean = String(email || "").trim().toLowerCase();
    if (!/^\d{6}$/.test(String(otp || "").trim())) {
      onError?.("Enter the 6-digit OTP sent to your email.");
      return false;
    }
    setBusy(true);
    onError?.("");
    try {
      await api.post("/auth/otp/verify", { email: clean, otp: String(otp).trim(), purpose });
      setVerified(true);
      setInfo("Email verified successfully.");
      return true;
    } catch (err) {
      onError?.(err.message || "Invalid OTP. Please try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [email, otp, purpose, onError]);

  return { otp, setOtp, sent, verified, busy, cooldown, info, send, verify };
}

export function EmailOtpStatus({ otpState }) {
  if (!otpState) return null;
  const { sent, verified, otp, setOtp, verify, busy, info } = otpState;

  if (verified) {
    return (
      <div className="p-3 rounded-3 mb-3 border bg-success bg-opacity-10 border-success-subtle">
        <div className="d-flex align-items-center gap-2 fw-bold text-success small">
          <CheckCircle size={16} />
          Email verified via OTP
        </div>
      </div>
    );
  }

  if (!sent) return null;

  return (
    <div className="p-3 rounded-3 mb-3 border bg-primary bg-opacity-10 border-primary-subtle">
      <label className="form-label small fw-bold text-secondary text-uppercase mb-1">
        Enter 6-digit OTP
      </label>
      <div className="input-group">
        <span className="input-group-text bg-body-tertiary">
          <KeyRound size={16} />
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          className="form-control"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000"
        />
        <button
          type="button"
          className="btn btn-primary fw-bold btn-sm"
          disabled={busy || String(otp).length !== 6}
          onClick={verify}
        >
          {busy ? "Verifying…" : "Verify OTP"}
        </button>
      </div>
      <div className="small text-secondary mt-2" style={{ fontSize: 12 }}>
        {info || "Check your inbox and spam/junk folder for the code. Valid for 10 minutes."}
      </div>
    </div>
  );
}

export function otpButtonLabel(otpState, fallback = "Send OTP") {
  if (!otpState) return fallback;
  if (otpState.verified) return "Verified";
  if (otpState.busy && !otpState.sent) return "Sending…";
  if (otpState.sent && otpState.cooldown > 0) return `Resend (${otpState.cooldown}s)`;
  if (otpState.sent) return "Resend OTP";
  return fallback;
}
